import { useEffect, useState } from "react";
import Lottie from "lottie-react";
import CryptoTickerCloud from "./CryptoTickerCloud";

type HeroAnimationProps = {
  className?: string;
  ariaLabel?: string;
};

// Lightweight hero animation with soft glow and transparent background.
// Attempts to load Lottie JSON from /animations/analysis.json in public.
// Falls back to an inline minimal SVG if not available.
export default function HeroAnimation({ className = "", ariaLabel = "Market analysis animation" }: HeroAnimationProps) {
  const [data, setData] = useState<any | null>(null);

  useEffect(() => {
    let mounted = true;
    fetch("/animations/analysis.json", { cache: "no-store" })
      .then(async (res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        if (mounted) setData(json);
      })
      .catch(() => {
        // Silently fall back to inline SVG without UI notice
      });
    return () => {
      mounted = false;
    };
  }, []);

  return (
    <div className={`relative flex items-center justify-center ${className}`}> 
      {/* Soft blue glow behind the animation for cohesion with hero accents */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute right-6 top-10 w-56 h-56 bg-[hsl(193,100%,50%)]/15 rounded-full blur-2xl" />
        <div className="absolute right-16 bottom-4 w-40 h-40 bg-[hsl(193,100%,60%)]/10 rounded-full blur-xl" />
      </div>

      <div className="relative z-10" role="img" aria-label={ariaLabel}>
        {data ? (
          <Lottie
            animationData={data}
            loop
            autoplay
            style={{ width: "28rem", height: "20rem" }}
          />
        ) : (
          <svg
            width="448"
            height="320"
            viewBox="0 0 448 320"
            xmlns="http://www.w3.org/2000/svg"
            className="rounded-xl"
            aria-hidden="true"
          >
            <defs>
              <linearGradient id="g" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor="#38bdf8" />
                <stop offset="100%" stopColor="#7dd3fc" />
              </linearGradient>
            </defs>
            <rect x="0" y="0" width="448" height="320" fill="transparent" />
            <g stroke="url(#g)" strokeWidth="2" fill="none" opacity="0.9">
              <path d="M16 260 C 80 220, 120 180, 180 200 S 280 260, 432 220">
                <animate attributeName="d" dur="4s" repeatCount="indefinite"
                  values="M16 260 C 80 220, 120 180, 180 200 S 280 260, 432 220;
                          M16 250 C 80 210, 120 190, 180 210 S 280 240, 432 210;
                          M16 260 C 80 220, 120 180, 180 200 S 280 260, 432 220" />
              </path>
              <path d="M16 200 C 80 160, 120 140, 180 150 S 280 180, 432 160" opacity="0.6">
                <animate attributeName="d" dur="5s" repeatCount="indefinite"
                  values="M16 200 C 80 160, 120 140, 180 150 S 280 180, 432 160;
                          M16 210 C 80 170, 120 150, 180 160 S 280 170, 432 150;
                          M16 200 C 80 160, 120 140, 180 150 S 280 180, 432 160" />
              </path>
              <circle cx="360" cy="96" r="22" strokeWidth="3" opacity="0.8">
                <animate attributeName="r" dur="3.2s" values="18;24;18" repeatCount="indefinite" />
              </circle>
            </g>
          </svg>
        )}
      </div>

      {/* Floating crypto tickers overlay (subtle, transparent, balanced) */}
      <CryptoTickerCloud className="z-20" opacity={0.8} />

      
    </div>
  );
}
